import apiFetch from "@wordpress/api-fetch";
import { useSelect } from "@wordpress/data";
import React from "react";
import { useQuery } from "react-query";
import { useDebounce } from "../../hooks";
import { preferencesStore } from "../../store/preferences-store";
import { LibraryDataResponse, TemplateData } from "../../types";

const useLibrary = () => {
	const libraryDataQuery = useQuery(["library-data"], () =>
		apiFetch<LibraryDataResponse>({
			path: `/magazine-blocks/v1/library-data`,
			method: "GET",
		})
	);
	const [contentType, setContentType] = React.useState<
		"sections" | "templates" | "starter-packs" | "favorites"
	>("sections");
	const [selectedCategory, setSelectedCategory] = React.useState("");
	const [searchQuery, setSearchQuery] = React.useState("");
	const [selectedStarterPack, setSelectedStarterPack] =
		React.useState<number>();
	const debouncedSearchQuery = useDebounce(searchQuery, 400);

	const categorizedData = Object.values(
		libraryDataQuery.data?.[
			"sections" === contentType
				? "categorized_sections"
				: "categorized_templates"
		] ?? {}
	).map((x) => {
		const items = x.items
			.filter((y) =>
				!debouncedSearchQuery
					? true
					: y.post_title
							.toLowerCase()
							.includes(debouncedSearchQuery.toLowerCase())
			)
			.flatMap((x) =>
				x.children.length && contentType === "templates"
					? x.children
					: x
			);

		return {
			...x,
			items,
			count: items.length,
		};
	});

	let templateMap = new Map<number, TemplateData>();

	Object.values(categorizedData)?.forEach((category) => {
		category.items.forEach((item: TemplateData) => {
			if (!templateMap.has(item.ID)) {
				templateMap.set(item.ID, item);
			}
		});
	});

	let templates = Array.from(templateMap.values());

	const favorites = useSelect(
		(select) => select(preferencesStore).getFavorites() as string[],
		[]
	);

	if (contentType == "favorites") {
		templates = Object.values(libraryDataQuery.data ?? {})
			.reduce((acc, curr) => {
				acc = [
					...acc,
					...Object.values(curr).reduce((a, c) => {
						a = [
							...a,
							...c.items
								.filter((x) => x.children.length)
								.flatMap((x) => x.children),
							...c.items.filter((x) => !x.children.length),
						];
						return a;
					}, [] as Array<TemplateData>),
				];
				return acc;
			}, [] as Array<TemplateData>)
			.filter(
				(x) =>
					(!debouncedSearchQuery
						? true
						: x.post_title
								.toLowerCase()
								.includes(
									debouncedSearchQuery.toLowerCase()
								)) &&
					-1 !== favorites.findIndex((y) => y === x.slug)
			);
	}

	const currentTemplates =
		selectedStarterPack && contentType === "starter-packs"
			? templates.find((t) => t.ID === selectedStarterPack)?.children ??
			  []
			: templates;

	const reset = () => {
		setSelectedCategory("");
		setSearchQuery("");
		setSelectedStarterPack(undefined);
	};

	return {
		reset,
		templates,
		currentTemplates,
		contentType,
		setContentType,
		selectedCategory,
		setSelectedCategory,
		searchQuery,
		debouncedSearchQuery,
		setSearchQuery,
		selectedStarterPack,
		setSelectedStarterPack,
		categorizedData,
		libraryDataQuery,
	};
};

export default useLibrary;
